import { Mic, MicOff } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { formatMinor } from "../lib/money";
import { parsePhrase } from "../lib/parser";
import type { OperationDraft, WorkspaceData } from "../lib/types";
import { TransactionRow } from "../components/TransactionRow";

interface DashboardViewProps {
  data: WorkspaceData;
  onDraft: (draft: OperationDraft) => void;
  onNavigate: (view: "history" | "accounts") => void;
}

const ACCOUNT_COLORS = ["#1fa969", "#e0ae2f", "#297bea", "#d6434e", "#2769ed", "#665de7"];
const CATEGORY_COLORS = ["#5ee6b3", "#79aaff", "#ffba66", "#c89fff", "#78909c"];

export function DashboardView({ data, onDraft, onNavigate }: DashboardViewProps) {
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const activeTransactions = data.transactions.filter((transaction) => !transaction.deletedAt);
  const activeAccounts = data.accounts.filter((account) => !account.archivedAt);
  const capital = data.accounts.reduce((sum, account) => sum + account.balanceMinor, 0n);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthTransactions = activeTransactions.filter((transaction) => transaction.occurredOn.startsWith(currentMonth));
  const monthIncome = monthTransactions
    .filter((transaction) => transaction.type === "income")
    .reduce((sum, transaction) => sum + transaction.amountMinor, 0n);
  const monthExpense = monthTransactions.reduce(
    (sum, transaction) => transaction.type === "expense" ? sum + transaction.amountMinor : transaction.type === "refund" ? sum - transaction.amountMinor : sum,
    0n,
  );
  const categoryTotals = useMemo(() => {
    const totals = new Map<string, bigint>();
    for (const transaction of monthTransactions) {
      if ((transaction.type === "expense" || transaction.type === "refund") && transaction.categoryId) {
        const sign = transaction.type === "expense" ? 1n : -1n;
        totals.set(transaction.categoryId, (totals.get(transaction.categoryId) ?? 0n) + transaction.amountMinor * sign);
      }
    }
    return [...totals]
      .map(([id, amount]) => ({ name: data.categories.find((category) => category.id === id)?.name ?? "Другое", amount }))
      .filter((item) => item.amount > 0n)
      .sort((left, right) => left.amount > right.amount ? -1 : 1)
      .slice(0, 5);
  }, [data.categories, monthTransactions]);
  const maxCategory = categoryTotals[0]?.amount ?? 1n;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!input.trim()) return;
    onDraft(parsePhrase(input, activeAccounts, data.categories, data.incomeSources).draft);
  }

  function startVoice() {
    const Recognition = (window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike;
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    }).SpeechRecognition ?? (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognitionLike }).webkitSpeechRecognition;
    if (!Recognition) {
      alert("Голосовой ввод не поддерживается этим браузером. Можно ввести фразу текстом.");
      return;
    }
    const recognition = new Recognition();
    recognition.lang = "ru-RU";
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => setInput(event.results[0][0].transcript);
    recognition.start();
  }

  return (
    <section className="view is-visible">
      <div className="dashboard-grid">
        <section className="balance-panel panel">
          <div className="balance-head">
            <div><p className="panel-label">Общий капитал</p><p className="total-balance">{formatMinor(capital)}</p></div>
            <span className="balance-change">Все свои счета</span>
          </div>
          <div className="month-stats" aria-label="Доходы и расходы текущего месяца">
            <div><span className="stat-label">Доходы</span><strong className="income">{formatMinor(monthIncome, true)}</strong></div>
            <div><span className="stat-label">Расходы</span><strong className="expense">{formatMinor(-monthExpense)}</strong></div>
            <div className="spark-bars" aria-hidden="true"><i style={{ height: "33%" }} /><i style={{ height: "51%" }} /><i style={{ height: "44%" }} /><i style={{ height: "77%" }} /><i style={{ height: "61%" }} /><i className="hot" style={{ height: "88%" }} /><i style={{ height: "69%" }} /></div>
          </div>
        </section>

        <section className="quick-entry panel">
          <div className="entry-copy"><span className="pulse-dot" /><div><p className="panel-label">Быстрая запись</p><h2>Что произошло с деньгами?</h2></div></div>
          <form className="entry-form" onSubmit={submit}>
            <textarea id="quick-entry-input" rows={2} value={input} onChange={(event) => setInput(event.target.value)} placeholder="Например: заправил машину на трёшку, оплатил с втб" />
            <div className="entry-actions">
              <button className={`voice-button ${listening ? "is-listening" : ""}`} type="button" onClick={startVoice} aria-label="Голосовой ввод">{listening ? <MicOff size={17} /> : <Mic size={17} />}</button>
              <span className="entry-hint">Можно обычными словами</span>
              <button className="submit-button" type="submit"><span>Распознать</span><span>→</span></button>
            </div>
          </form>
          <div className="example-chips">
            <button type="button" onClick={() => setInput("заправил машину на трёшку, оплатил с втб")}>Заправка с ВТБ</button>
            <button type="button" onClick={() => setInput("перевёл со сбера на альфу 15000 рублей")}>Перевод 15 000 ₽</button>
          </div>
        </section>

        <section className="accounts-panel panel">
          <div className="section-head"><div><p className="panel-label">Где лежат деньги</p><h2>Счета</h2></div><button className="text-button" type="button" onClick={() => onNavigate("accounts")}>Все счета</button></div>
          {activeAccounts.length ? (
            <div className="accounts-list">
              {activeAccounts.slice(0, 6).map((account, index) => (
                <article className="account-card" key={account.accountId}>
                  <div className="account-top"><span className="account-logo" style={{ "--logo-color": ACCOUNT_COLORS[index % ACCOUNT_COLORS.length] } as React.CSSProperties}>{account.name.at(0)}</span><span className="account-name">{account.name}</span></div>
                  <strong className="account-balance">{formatMinor(account.balanceMinor)}</strong>
                </article>
              ))}
            </div>
          ) : <EmptyAccounts onNavigate={() => onNavigate("accounts")} />}
        </section>

        <section className="recent-panel panel">
          <div className="section-head"><div><p className="panel-label">Последние движения</p><h2>История</h2></div><button className="text-button" type="button" onClick={() => onNavigate("history")}>Вся история</button></div>
          <div className="transaction-list">
            {activeTransactions.slice(0, 4).map((transaction) => <TransactionRow key={transaction.id} transaction={transaction} accounts={data.accounts} />)}
            {!activeTransactions.length && <div className="empty-state"><span>Операций пока нет</span><small>Запишите первое движение обычной фразой</small></div>}
          </div>
        </section>

        <section className="categories-panel panel">
          <div className="section-head"><div><p className="panel-label">Текущий месяц</p><h2>Куда уходят деньги</h2></div></div>
          <div className="category-list">
            {categoryTotals.map((category, index) => (
              <div className="category-item" key={category.name}>
                <div className="category-head"><span>{category.name}</span><strong>{formatMinor(category.amount)}</strong></div>
                <div className="category-track"><i style={{ "--width": `${Number((category.amount * 100n) / maxCategory)}%`, "--bar-color": CATEGORY_COLORS[index % CATEGORY_COLORS.length] } as React.CSSProperties} /></div>
              </div>
            ))}
            {!categoryTotals.length && <div className="empty-state"><span>Расходов пока нет</span><small>Здесь появится распределение по категориям</small></div>}
          </div>
        </section>
      </div>
    </section>
  );
}

function EmptyAccounts({ onNavigate }: { onNavigate: () => void }) {
  return <div className="empty-state compact-empty"><span>Добавьте первую карту, счёт или наличные</span><button className="secondary-button" type="button" onClick={onNavigate}>Добавить счёт</button></div>;
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  onstart: () => void;
  onend: () => void;
  onerror: () => void;
  onresult: (event: { results: ArrayLike<{ [index: number]: { transcript: string } }> }) => void;
  start: () => void;
}
